import { fetcher } from '@rocketlane/state-db';
import { PlanBoardAPI } from 'api/plan-board';
import { mergeTasksIntoCollection } from 'collections/tasks';
import { logger } from 'services';
import { attachPhaseProgressReconciler } from './phase-progress-reconciler';
import { planFunnel } from './telemetry';
import { attachPlanLaneReconciler, getPlanFiltersHash, getPlanLaneKey, planBoardPhaseCollection, planLaneCollection } from './plan-lane-collection';
/**
 * READ layer for the paginated plan board. Every server payload flows through
 * exactly ONE writer (`ingestLanePage`): page tasks + subtasks land in
 * `taskCollection` (merge-based — page payloads are metaData-stripped and
 * must never downgrade richer entries) and the lane record (ids + cursor +
 * counts, NO entities) lands in `planLaneCollection` in the same synchronous
 * block. Single code path to audit for token↔entity consistency.
 */ const PAGE_SIZE = 25;
// Must match the `staleMs` the lane / board `useCollectionQuery`s use (see
// collections/plan-board/hooks.ts). A forced refresh re-arms the same freshness
// window so the mounted query doesn't immediately refetch on top of it.
const LANE_STALE_MS = 10 * 60 * 1000;
export const getPlanLaneFetchKey = (laneKey)=>`plan-lane:${laneKey}`;
export const getPlanBoardFetchKey = (projectId, filters)=>`plan-board:${projectId}:${getPlanFiltersHash(filters)}`;
const BATCH_MAX_PHASES = 12;
/**
 * Lane mounts are staggered one per frame by the frame sequencer, so the
 * coalesce window must span several frames to catch a viewport's worth of
 * lanes in one batch.
 */ const BATCH_WINDOW_MS = 100;
const pendingBatches = new Map();
const flushBatch = (batchKey)=>{
    const batch = pendingBatches.get(batchKey);
    if (!batch) {
        return;
    }
    pendingBatches.delete(batchKey);
    clearTimeout(batch.timer);
    const phaseIds = Array.from(batch.phases.keys());
    PlanBoardAPI.getPhaseEntitiesBatch(batch.projectId, phaseIds, {
        limit: PAGE_SIZE,
        filters: batch.filters
    }).then((response)=>{
        var _response_pages;
        const pagesByPhase = new Map(((_response_pages = response.pages) !== null && _response_pages !== void 0 ? _response_pages : []).map((page)=>[
                page.projectPhaseId,
                page
            ]));
        batch.phases.forEach((requests, phaseId)=>{
            const page = pagesByPhase.get(phaseId);
            requests.forEach(({ resolve, reject })=>{
                if (page) {
                    resolve(page);
                } else {
                    // Phase omitted by the server (not part of the project / deleted).
                    reject(new Error(`Phase ${phaseId} missing from batch entities response`));
                }
            });
        });
    }).catch((error)=>{
        batch.phases.forEach((requests)=>{
            requests.forEach(({ reject })=>reject(error));
        });
    });
};
const loadFirstEntitiesPage = (projectId, phaseId, filters)=>{
    const batchKey = `${projectId}:${getPlanFiltersHash(filters)}`;
    return new Promise((resolve, reject)=>{
        var _batch_phases_get;
        let batch = pendingBatches.get(batchKey);
        if (!batch) {
            batch = {
                projectId,
                filters,
                phases: new Map(),
                timer: setTimeout(()=>flushBatch(batchKey), BATCH_WINDOW_MS)
            };
            pendingBatches.set(batchKey, batch);
        }
        const requests = (_batch_phases_get = batch.phases.get(phaseId)) !== null && _batch_phases_get !== void 0 ? _batch_phases_get : [];
        requests.push({
            resolve,
            reject
        });
        batch.phases.set(phaseId, requests);
        if (batch.phases.size >= BATCH_MAX_PHASES) {
            flushBatch(batchKey);
        }
    });
};
// ─── The single writer ───────────────────────────────────────────────────────
/**
 * Land one entities page: tasks + subtasks into `taskCollection`
 * (merge-based), the id window + cursor + counts onto the lane record.
 * `mode: 'replace'` (first page / refresh) resets the window; `'append'`
 * (cursor page) extends it, deduping tokens already present (a refetch racing
 * a load-more must not duplicate rows).
 */ const ingestLanePage = (projectId, phaseId, filters, page, mode)=>{
    var _page_tasks, _page_subTasks, _page_pageTokens, _page_sprints, _page_sprints1, _page_totalCount, _page_filteredCount;
    attachPlanLaneReconciler();
    attachPhaseProgressReconciler();
    mergeTasksIntoCollection([
        ...(_page_tasks = page.tasks) !== null && _page_tasks !== void 0 ? _page_tasks : [],
        ...(_page_subTasks = page.subTasks) !== null && _page_subTasks !== void 0 ? _page_subTasks : []
    ]);
    const laneKey = getPlanLaneKey(projectId, phaseId, filters);
    const existing = planLaneCollection.get(laneKey);
    const incomingTokens = (_page_pageTokens = page.pageTokens) !== null && _page_pageTokens !== void 0 ? _page_pageTokens : [];
    const pageTokens = mode === 'append' && existing ? [
        ...existing.pageTokens,
        ...incomingTokens.filter((token)=>!existing.pageTokens.includes(token))
    ] : incomingTokens;
    const sprints = mode === 'append' && existing ? [
        ...existing.sprints,
        ...((_page_sprints = page.sprints) !== null && _page_sprints !== void 0 ? _page_sprints : []).filter((sprint)=>!existing.sprints.some((s)=>s.sprintId === sprint.sprintId))
    ] : (_page_sprints1 = page.sprints) !== null && _page_sprints1 !== void 0 ? _page_sprints1 : [];
    const lane = {
        laneKey,
        projectId,
        projectPhaseId: phaseId,
        filtersHash: getPlanFiltersHash(filters),
        pageTokens,
        nextCursor: page.nextCursor,
        nextCursorIndex: page.nextCursorIndex,
        totalCount: (_page_totalCount = page.totalCount) !== null && _page_totalCount !== void 0 ? _page_totalCount : 0,
        filteredCount: (_page_filteredCount = page.filteredCount) !== null && _page_filteredCount !== void 0 ? _page_filteredCount : 0,
        sprints,
        isLoadingMore: false
    };
    planLaneCollection.push([
        lane
    ]);
    return lane;
};
// ─── Fetchers ────────────────────────────────────────────────────────────────
/**
 * First entities page of a lane (batch-coalesced). Used as the `queryFn` of
 * the lane's `useCollectionQuery` — staleness/dedup handled by the shared
 * `fetcher` under `getPlanLaneFetchKey`.
 */ export const fetchLaneFirstPage = async (projectId, phaseId, filters)=>{
    const page = await loadFirstEntitiesPage(projectId, phaseId, filters);
    return ingestLanePage(projectId, phaseId, filters, page, 'replace');
};
/**
 * Next cursor page of a lane (direct per-phase GET). No-ops when the lane has
 * no cursor (fully loaded) or a load-more is already in flight.
 */ export const fetchLaneNextPage = async (projectId, phaseId, filters)=>{
    const laneKey = getPlanLaneKey(projectId, phaseId, filters);
    const lane = planLaneCollection.get(laneKey);
    if (!(lane === null || lane === void 0 ? void 0 : lane.nextCursor) || lane.isLoadingMore) {
        return lane;
    }
    planLaneCollection.push([
        {
            ...lane,
            isLoadingMore: true
        }
    ]);
    try {
        var _page_pageTokens;
        const page = await PlanBoardAPI.getPhaseEntities(projectId, phaseId, {
            cursor: {
                cursor: lane.nextCursor,
                cursorIndex: lane.nextCursorIndex
            },
            limit: PAGE_SIZE,
            filters
        });
        const newTokenCount = ((_page_pageTokens = page.pageTokens) !== null && _page_pageTokens !== void 0 ? _page_pageTokens : []).filter((token)=>!lane.pageTokens.includes(token)).length;
        if (newTokenCount === 0) {
            // DEVIATION: load-more fired but the page brought no new tokens
            // (cursor pointing past the end, or a fully-overlapping window).
            planFunnel.pageEmpty({
                phaseId
            });
        } else {
            planFunnel.pageLoadMore({
                phaseId,
                loaded: newTokenCount
            });
        }
        return ingestLanePage(projectId, phaseId, filters, page, 'append');
    } catch (error) {
        planFunnel.pageError({
            phaseId
        });
        const current = planLaneCollection.get(laneKey);
        if (current) {
            planLaneCollection.push([
                {
                    ...current,
                    isLoadingMore: false
                }
            ]);
        }
        throw error;
    }
};
/**
 * The plan filters the board was last fetched with, per project — refresh
 * paths (mutation reconciliation) don't know the view's active filters, so
 * they re-fetch with these to keep `filteredEntityCount` coherent with what
 * the board is currently displaying.
 */ const lastBoardFilters = new Map();
/**
 * Board aggregates (per-phase counts) → `planBoardPhaseCollection`. Any
 * `firstPage` payloads the board response carries are ingested as lane first
 * pages too (same single writer). With `filters`, the server also returns a
 * per-phase `filteredEntityCount` (tasks filtered, sprints always included)
 * used for lane visibility under the budget filter.
 */ export const fetchPlanBoard = async (projectId, filters)=>{
    var _response_phases, _response_phases1;
    attachPhaseProgressReconciler();
    lastBoardFilters.set(projectId, filters);
    const response = await PlanBoardAPI.getBoard(projectId, {
        filters
    });
    const entries = ((_response_phases = response.phases) !== null && _response_phases !== void 0 ? _response_phases : []).filter((entry)=>{
        var _entry_phase;
        return ((_entry_phase = entry.phase) === null || _entry_phase === void 0 ? void 0 : _entry_phase.projectPhaseId) != null;
    }).map((entry)=>{
        var _entry_entityCount, _entry_filteredEntityCount;
        return {
            projectPhaseId: entry.phase.projectPhaseId,
            projectId,
            phase: entry.phase,
            entityCount: (_entry_entityCount = entry.entityCount) !== null && _entry_entityCount !== void 0 ? _entry_entityCount : 0,
            filteredEntityCount: (_entry_filteredEntityCount = entry.filteredEntityCount) !== null && _entry_filteredEntityCount !== void 0 ? _entry_filteredEntityCount : undefined
        };
    });
    planBoardPhaseCollection.push(entries);
    ((_response_phases1 = response.phases) !== null && _response_phases1 !== void 0 ? _response_phases1 : []).forEach((entry)=>{
        var _entry_phase;
        if (entry.firstPage && ((_entry_phase = entry.phase) === null || _entry_phase === void 0 ? void 0 : _entry_phase.projectPhaseId) != null) {
            ingestLanePage(projectId, entry.phase.projectPhaseId, filters, entry.firstPage, 'replace');
            fetcher.markFresh(getPlanLaneFetchKey(getPlanLaneKey(projectId, entry.phase.projectPhaseId, filters)));
        }
    });
};
/** Reconstruct the filters object a lane was keyed under from its hash. */ const filtersFromHash = (filtersHash)=>{
    const [completed, responsibleCompanyIds, includePrivate, budgetIds] = JSON.parse(filtersHash);
    const filters = {
        ...completed !== null && {
            completed
        },
        ...responsibleCompanyIds.length > 0 && {
            responsibleCompanyIds
        },
        ...includePrivate !== true && {
            includePrivate
        },
        ...budgetIds.length > 0 && {
            budgetIds
        }
    };
    return Object.keys(filters).length > 0 ? filters : undefined;
};
// ─── Invalidation / refresh ──────────────────────────────────────────────────
/**
 * Centralized "the plan board changed shape" refresh — replaces the raw
 * `queryClient.invalidateQueries(['phase-entities', projectId])` +
 * `(['plan-board', projectId])` pairs scattered across mutation call sites.
 *
 * Marks every known lane of the project stale and refetches the ones that
 * have records (i.e. lanes some view has loaded) — the per-lane first-page
 * fetches coalesce through the batch loader, so a whole board refresh costs
 * one or two batch POSTs. A refreshed lane resets to its first page
 * (load-more depth is re-fetched on scroll — same practical behavior as the
 * RQ invalidation this replaces).
 */ /**
 * Re-fetch the board with the filters it was last fetched with — for
 * mutation-reconciliation callers that don't know the view's active filters.
 */ export const refreshPlanBoard = (projectId)=>fetchPlanBoard(projectId, lastBoardFilters.get(projectId));
export const refreshProjectPlanLanes = async (projectId)=>{
    const filters = lastBoardFilters.get(projectId);
    const lanes = planLaneCollection.getByIndex('byProject', projectId);
    // Never rejects: this is a background reconciliation helper called
    // fire-and-forget from unrelated mutation onSuccess paths — a refresh
    // failure must not fail (or unhandled-reject) the caller. Failures are
    // logged; the stale lanes refetch on their next natural trigger.
    //
    // Route every refetch THROUGH the shared fetcher (force=true) under the
    // SAME key the mounted `useCollectionQuery` uses. Two reasons:
    //  - dedup: if a lane's query fetch is already in flight, this joins its
    //    promise instead of issuing a second /phase-entities/batch for the same
    //    phase (a bare fetchLaneFirstPage bypassed the fetcher → double batch).
    //  - freshness: on resolve the key is marked fresh, so the mounted query's
    //    next doFetch skips instead of refetching what we just refetched.
    const settled = await Promise.allSettled([
        fetcher.fetch(getPlanBoardFetchKey(projectId, filters), ()=>fetchPlanBoard(projectId, filters), {
            staleMs: LANE_STALE_MS
        }, true),
        ...lanes.map((lane)=>fetcher.fetch(getPlanLaneFetchKey(lane.laneKey), ()=>fetchLaneFirstPage(lane.projectId, lane.projectPhaseId, filtersFromHash(lane.filtersHash)), {
                staleMs: LANE_STALE_MS
            }, true))
    ]);
    settled.forEach((result)=>{
        if (result.status === 'rejected') {
            logger.error('plan-board refreshProjectPlanLanes partial failure', {
                error: result.reason,
                projectId
            });
        }
    });
};
/** Refresh a single phase's lanes (all filter variants currently loaded). */ export const refreshPlanLanesForPhase = async (projectId, phaseId)=>{
    const lanes = planLaneCollection.getByIndex('byPhase', phaseId).filter((lane)=>lane.projectId === projectId);
    // Never rejects — see refreshProjectPlanLanes. Route through the shared
    // fetcher (force=true) under the mounted query's key so the imperative
    // refresh and the lane's own useCollectionQuery share one in-flight promise
    // (dedup) and the query skips its next fetch (freshness) — otherwise a create
    // fired TWO /phase-entities/batch calls for the same phase.
    const settled = await Promise.allSettled(lanes.map((lane)=>fetcher.fetch(getPlanLaneFetchKey(lane.laneKey), ()=>fetchLaneFirstPage(lane.projectId, lane.projectPhaseId, filtersFromHash(lane.filtersHash)), {
            staleMs: LANE_STALE_MS
        }, true)));
    settled.forEach((result)=>{
        if (result.status === 'rejected') {
            logger.error('plan-board refreshPlanLanesForPhase partial failure', {
                error: result.reason,
                projectId,
                phaseId
            });
        }
    });
};
